import React, { useEffect, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Badge,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Typography
} from "@mui/material";
import { ExpandMore, Menu as MenuIcon, Close } from "@mui/icons-material";

const WP_URL = process.env.REACT_APP_WP_URL || "https://mycover-up.fr";
const WP_PRO_URL = process.env.REACT_APP_WP_PRO_URL || "https://cacheconfig.fr";

function ResponsiveAppBar({ setAppBarHeight, currentStep, logoUrl, isPro = false }) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [discoverOpen, setDiscoverOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [cartCount, setCartCount] = React.useState(0);
  const LISTITEMTEXT_SLOTPROPS_PRIMARY = {
    primary: {
      sx: {
        color: "#000",
        fontFamily: "Fira Sans, Helvetica, Arial, Lucida, sans-serif",
        fontSize: "13px",
        fontWeight: "500 !important",
      },
    },
  };
  const appBarRef = useRef(null);

  useEffect(() => {
    if (appBarRef.current) {
      setAppBarHeight(appBarRef.current.offsetHeight);
      const handleResize = () => {
        if (appBarRef.current) {
          setAppBarHeight(appBarRef.current.offsetHeight);
        }
      };
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, [setAppBarHeight]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const discoverLinks = [
    {
      label: "À propos",
      url: `${WP_URL}/decouvrir-mycoverup/`,
    },
    {
      label: "Nos réalisations",
      url: `${WP_URL}/nos-realisations/`,
    },
    {
      label: "Procédé de fabrication de nos caches",
      url: `${WP_URL}/procede-de-fabrication-de-nos-caches/`,
    },
    {
      label: "Composite aluminium : le matériau de nos caches",
      url: `${WP_URL}/le-composite-aluminium-le-materiau-de-nos-caches/`,
    },
    {
      label: "Télécharger notre brochure",
      url: `${WP_URL}/wp-content/uploads/2025/09/myCoverUp_Cache_clim.pdf`,
      download: true,
    },
  ];

  const discoverLinksMobile = [
    {
      label: "À propos",
      url: `${WP_URL}/decouvrir-mycoverup/`,
    },
    {
      label: "Nos réalisations",
      url: `${WP_URL}/nos-realisations/`,
    },
    {
      label: "Procédé de fabrication de nos caches",
      url: `${WP_URL}/procede-de-fabrication-de-nos-caches/`,
    },
    {
      label: "Catalogue PDF",
      url: `${WP_URL}/wp-content/uploads/2025/09/myCoverUp_Cache_clim.pdf`,
      download: true,
    },
  ];



  function getCartItemCountFromCookie() {
    const fragmentsKeys = Object.keys(sessionStorage).filter((key) =>
      key.startsWith("wc_fragments_")
    );

    if (fragmentsKeys.length === 0) return 0;

    const key = fragmentsKeys[0];
    const value = sessionStorage.getItem(key);

    if (!value) return 0;

    try {
      const parsed = JSON.parse(value);
      const html =
        parsed[".elementor-menu-cart__toggle_button span.elementor-button-icon-qty"];
      const match = html?.match(/data-counter="?(\d+)/);
      return match ? parseInt(match[1], 10) : 0;
    } catch (err) {
      console.error("❌ Erreur parsing wc_fragment :", err);
      return 0;
    }
  }

  useEffect(() => {
    setCartCount(getCartItemCountFromCookie());
  }, []);


  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
    if (mobileOpen) {
      setDiscoverOpen(false);
    }
  };

  const handleDiscoverToggle = () => {
    setDiscoverOpen((prev) => !prev);
  };

  const handleDiscoverClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDiscoverClose = () => {
    setAnchorEl(null);
  };

  // MENU MOBILE personnalisé (avec Collapse)
  const mobileMenu = (
    <Collapse in={mobileOpen} timeout="auto" unmountOnExit>
      <Box
        sx={{
          width: "100%",
          backgroundColor: "white",
          pt: 1,
        }}
      >
        <List sx={{ p: 0 }}>
          {/* Bouton "Créer mon cache" */}
          <ListItem sx={{ p: 0 }}>
            <Button
              href={`${WP_URL}/`}
              sx={{
                backgroundColor: "#2a7ac3 !important",
                width: "fit-content",
                borderRadius: "50px",
                margin: "10px",
                color: "#fff !important",
                textTransform: "none",
                fontFamily: "Fira Sans, Helvetica, Arial, Lucida, sans-serif",
                fontSize: "13px",
                fontWeight: "500 !important",
                py: 1,
                px: 3,
              }}
            >
              Créer mon cache
            </Button>
          </ListItem>
          <ListItem
            button
            component="a"
            href={`${WP_URL}/catalogue-mycoverup/`}
            sx={{
              backgroundColor: "transparent",
              color: "#000",
              fontFamily: "Fira Sans, Helvetica, Arial, Lucida, sans-serif",
              fontSize: "13px",
              fontWeight: "500 !important",
              py: 0.5,
              px: 2,
            }}
          >
            <ListItemText
              primary="Catalogue"
              slotProps={LISTITEMTEXT_SLOTPROPS_PRIMARY}
            />
          </ListItem>
          <ListItem sx={{ p: 0, mb: 1 }}>
            <Button
              href={`${WP_URL}/support-mycover-up/#montage`}
              sx={{
                backgroundColor: "#f4f4f4 !important",
                width: "fit-content",
                borderRadius: "50px",
                margin: "10px",
                color: "#33373d !important",
                textTransform: "none",
                fontFamily: "Fira Sans, Helvetica, Arial, Lucida, sans-serif",
                fontSize: "13px",
                fontWeight: "500 !important",
                py: 1,
                px: 3,
              }}
            >
              Monter mon cache ▶️
            </Button>
          </ListItem>
          <ListItem
            button
            onClick={handleDiscoverToggle}
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              backgroundColor: "transparent",
              color: "#000",
              py: 0.5,
              px: 2,
              gap: "4px",
            }}
          >
            <ListItemText
              primary="Découvrir myCover'Up"
              sx={{
                display: "inline",
                m: 0,
                p: 0,
                color: "#000",
              }}
              slotProps={LISTITEMTEXT_SLOTPROPS_PRIMARY}
            />
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                pr: "35%",
              }}
            >
              <svg
                className="e-font-icon-svg e-fas-caret-down"
                viewBox="0 0 320 512"
                xmlns="http://www.w3.org/2000/svg"
                style={{ width: "14px", height: "14px" }}
              >
                <path d="M31.3 192h257.3c17.8 0 26.7 21.5 14.1 34.1L174.1 354.8c-7.8 7.8-20.5 7.8-28.3 0L17.2 226.1C4.6 213.5 13.5 192 31.3 192z" />
              </svg>
            </Box>
          </ListItem>
          <Collapse in={discoverOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {discoverLinksMobile.map((link) => (
                <ListItem
                  button
                  key={link.label}
                  component="a"
                  href={link.url}
                  {...(link.download && { download: true, target: "_blank" })}
                  sx={{
                    pl: 4,
                    py: 0.5,
                    backgroundColor: "#f0f0f0",
                    fontFamily:
                      "Fira Sans, Helvetica, Arial, Lucida, sans-serif",
                    fontSize: "13px !important",
                    fontWeight: "500 !important",
                    color: "#000",
                  }}
                >
                  <ListItemText
                    primary={link.label}
                    slotProps={LISTITEMTEXT_SLOTPROPS_PRIMARY}
                  />
                </ListItem>
              ))}

            </List>
          </Collapse>
          <ListItem
            button
            component="a"
            href={`${WP_URL}/mon-compte/`}
            sx={{
              backgroundColor: "transparent",
              color: "#000",
              fontFamily: "Fira Sans, Helvetica, Arial, Lucida, sans-serif",
              fontSize: "13px",
              fontWeight: "500 !important",
              py: 0.5,
              px: 2,
            }}
          >
            <ListItemText
              primary="Espace Client"
              slotProps={LISTITEMTEXT_SLOTPROPS_PRIMARY}
            />
          </ListItem>
          <ListItem
            button
            component="a"
            href={`${WP_URL}/contact/`}
            sx={{
              backgroundColor: "transparent",
              color: "#000",
              fontFamily: "Fira Sans, Helvetica, Arial, Lucida, sans-serif",
              fontSize: "13px",
              fontWeight: "500 !important",
              py: 0.5,
              px: 2,
            }}
          >
            <ListItemText
              primary="Contact"
              slotProps={LISTITEMTEXT_SLOTPROPS_PRIMARY}
            />
          </ListItem>
          <ListItem
            button
            component="a"
            href={`${WP_URL}/bons-plans/`}
            sx={{
              backgroundColor: "transparent",
              color: "#000",
              fontFamily: "Fira Sans, Helvetica, Arial, Lucida, sans-serif",
              fontSize: "13px",
              fontWeight: "500 !important",
              py: 0.5,
              px: 2,
            }}
          >
            <ListItemText
              primary={
                <Typography
                  sx={{
                    color: "#000",
                    fontFamily: "Fira Sans, Helvetica, Arial, Lucida, sans-serif",
                    fontSize: "13px",
                    fontWeight: 500,
                  }}
                >
                  Bons plans
                </Typography>
              }
            />
            <ListItemText
            />
          </ListItem>
        </List>
      </Box>
    </Collapse>
  );

  if (isPro) {
    return (
      <AppBar
        ref={appBarRef}
        position="fixed"
        sx={{
          backgroundColor: "white",
          color: "black",
          boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.4)",
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            position: "relative",
            height: "75px",
            px: 2,
          }}
        >
          {/* LOGO CENTRÉ */}
          <Box
            sx={{
              margin: "0 auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src={logoUrl}
              alt="Logo"
              sx={{
                height: { xs: "4vh", sm: "4vh", md: "4vh" },
                maxWidth: "150px",
                objectFit: "contain",
              }}
            />
          </Box>

          {/* BOUTON À DROITE EN ABSOLU */}
          <Box
            sx={{
              position: "absolute",
              right: 16,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            <Button
              disableRipple
              href={`${WP_PRO_URL}/`}
              sx={{
                textTransform: "none",
                color: "#000",
                fontFamily: '"Fira Sans", Sans-serif',
                fontSize: "13px",
                fontWeight: "500 !important",
                backgroundColor: "transparent",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "#007ACE",
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: "3px",
                  backgroundColor: "transparent",
                  transform: "scaleX(0)",
                  transformOrigin: "center",
                  transition: "transform 0.3s ease-in-out",
                },
                "&:hover::after": {
                  backgroundColor: "#007ACE",
                  transform: "scaleX(1)",
                },
              }}
            >
              Mon compte
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

    );
  }

  return (
    <AppBar
      ref={appBarRef}
      position="fixed"
      sx={{
        backgroundColor: "white",
        color: "black",
        boxShadow: isMobile ? "none" : "0px 8px 20px rgba(0, 0, 0, 0.4)",
      }}
    >
      {isMobile ? (
        <>
          <Toolbar
            disableGutters
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-around",
              minHeight: "inherit",
              height: "52px",
              ml: 1.5,
            }}
          >
            <IconButton
              edge="start"
              color="inherit"
              aria-label="toggle menu"
              onClick={handleDrawerToggle}
              sx={{
                backgroundColor: "#F2F2F2",
                borderRadius: "4px",
                padding: "6px",
              }}
            >
              {mobileOpen ? (
                <Close />
              ) : (
                <img
                  loading="lazy"
                  src={`${process.env.PUBLIC_URL}/icons/menu-custom.svg`}
                  alt="Menu"
                  style={{ width: 21, height: 21, fill: "#33373d" }}
                />
              )}

            </IconButton>

            <Box
              component="a"
              href={`${WP_URL}/`}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/logo-horizontal-couleur-300x83.png`}
                alt="Logo"
                sx={{
                  maxWidth: "161px",
                }}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton
                href={`${WP_URL}/espace-client/`}
                sx={{ color: "#007bff" }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                  style={{ width: 17, height: 17 }}
                >
                  <path
                    fill="#007AC3"
                    d="M248 8C111 8 0 119 0 256s111 248 248 248 248-111 248-248S385 8 248 8zm0 96c48.6 0 88 39.4 88 88s-39.4 88-88 88-88-39.4-88-88 39.4-88 88-88zm0 344c-58.7 0-111.3-26.6-146.5-68.2 18.8-35.4 55.6-59.8 98.5-59.8 2.4 0 4.8.4 7.1 1.1 13 4.2 26.6 6.9 40.9 6.9 14.3 0 28-2.7 40.9-6.9 2.3-.7 4.7-1.1 7.1-1.1 42.9 0 79.7 24.4 98.5 59.8C359.3 421.4 306.7 448 248 448z"
                  />
                </svg>
              </IconButton>
              <IconButton
                sx={{ pl: 1.5 }}
                href={`${WP_URL}/mon-panier/`}
              >
                <Badge
                  badgeContent={cartCount}
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: "#d9534f",
                      color: "white",
                      fontSize: "12px",
                      minWidth: "18px",
                      height: "18px",
                    },
                  }}
                >
                  <img
                    loading="lazy"
                    src={`${process.env.PUBLIC_URL}/icons/cart_icon.svg`}
                    alt="Cart Icon"
                    style={{ width: 17, height: 17 }}
                  />
                </Badge>

              </IconButton>

            </Box>
          </Toolbar>
          {mobileMenu}
        </>
      ) : (
        <>
          <Toolbar
            disableGutters
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              minHeight: "inherit",
              height: "75px",
              paddingBottom: "6px",
            }}
          >
            <Box
              sx={{

                display: "flex",
                width: "90%",
                alignItems: "center",
                justifyContent: "space-between",
                position: "relative",
              }}
            >
              <Box
                component="a"
                href={`${WP_URL}/`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "17%",
                }}
              >
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL}/logo-horizontal-couleur.png`}
                  alt="Logo"
                  sx={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "center",
                  width: "80%",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  href={`${WP_URL}/`}
                  sx={{
                    backgroundColor: theme.palette.custom.blue,
                    borderRadius: "20px",
                    color: "#fff",
                    boxShadow: "none !important",
                    textTransform: "none",
                    fontFamily: '"Fira Sans", Sans-serif',
                    fontSize: "13px",
                    fontWeight: "500 !important",
                    "&:hover": {
                      boxShadow: "none !important",
                      backgroundColor: "#fff",
                      color: "#007ACE",
                    },
                  }}
                >
                  Créer mon cache
                </Button>

                <Button
                  variant="contained"
                  href={`${WP_URL}/support-mycover-up/#montage`}
                  sx={{
                    backgroundColor: "#f4f4f4",
                    borderRadius: "20px",
                    color: "#000",
                    boxShadow: "none !important",
                    textTransform: "none",
                    fontFamily: '"Fira Sans", Sans-serif',
                    fontSize: "13px",
                    fontWeight: "500 !important",
                    "&:hover": {
                      backgroundColor: "#007ACE",
                      boxShadow: "none !important",
                      color: "#fff",
                    },
                  }}
                >
                  Monter mon cache ▶️
                </Button>

                <Box
                  sx={{
                    display: "flex",
                    gap: 0,
                    justifyContent: "center",
                  }}
                >
                  <Button
                    disableRipple
                    href={`${WP_URL}/catalogue-mycoverup/`}
                    sx={{
                      textTransform: "none",
                      color: "#000",
                      fontFamily: '"Fira Sans", Sans-serif',
                      fontSize: "13px",
                      fontWeight: "500 !important",
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: "#007ACE",
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "3px",
                        backgroundColor: "transparent",
                        transform: "scaleX(0)",
                        transformOrigin: "center",
                        transition: "transform 0.3s ease-in-out",
                      },
                      "&:hover::after": {
                        backgroundColor: "#007ACE",
                        transform: "scaleX(1)",
                      },
                    }}
                  >
                    Catalogue
                  </Button>
                  <Button
                    disableRipple
                    onClick={handleDiscoverClick}
                    endIcon={<ExpandMore />}
                    sx={{
                      textTransform: "none",
                      color: "#000",
                      fontFamily: '"Fira Sans", Sans-serif',
                      fontSize: "13px",
                      fontWeight: "500 !important",
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: "#007ACE",
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "3px",
                        backgroundColor: "transparent",
                        transform: "scaleX(0)",
                        transformOrigin: "center",
                        transition: "transform 0.3s ease-in-out",
                      },
                      "&:hover::after": {
                        backgroundColor: "#007ACE",
                        transform: "scaleX(1)",
                      },
                    }}
                  >
                    Découvrir myCover'Up
                  </Button>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleDiscoverClose}
                    MenuListProps={{
                      "aria-labelledby": "discover-button",
                    }}
                  >
                    {discoverLinks.map((link) => (
                      <MenuItem
                        key={link.label}
                        onClick={handleDiscoverClose}
                        component="a"
                        href={link.url}
                        {...(link.download && { download: true, target: "_blank" })}
                        sx={{
                          fontFamily: '"Fira Sans", Sans-serif',
                          fontSize: "13px",
                          fontWeight: "500 !important",
                          "&:hover": {
                            color: "#007bff",
                          },
                        }}
                      >
                        {link.label}
                      </MenuItem>
                    ))}

                  </Menu>
                  <Button
                    disableRipple
                    href={`${WP_URL}/espace-client/`}
                    sx={{
                      textTransform: "none",
                      color: "#000",
                      fontFamily: '"Fira Sans", Sans-serif',
                      fontSize: "13px",
                      fontWeight: "500 !important",
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: "#007ACE",
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "3px",
                        backgroundColor: "transparent",
                        transform: "scaleX(0)",
                        transformOrigin: "center",
                        transition: "transform 0.3s ease-in-out",
                      },
                      "&:hover::after": {
                        backgroundColor: "#007ACE",
                        transform: "scaleX(1)",
                      },
                    }}
                  >
                    Espace Client
                  </Button>
                  <Button
                    disableRipple
                    href={`${WP_URL}/contact/`}
                    sx={{
                      textTransform: "none",
                      color: "#000",
                      fontFamily: '"Fira Sans", Sans-serif',
                      fontSize: "13px",
                      fontWeight: "500 !important",
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: "#007ACE",
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "3px",
                        backgroundColor: "transparent",
                        transform: "scaleX(0)",
                        transformOrigin: "center",
                        transition: "transform 0.3s ease-in-out",
                      },
                      "&:hover::after": {
                        backgroundColor: "#007ACE",
                        transform: "scaleX(1)",
                      },
                    }}
                  >
                    Contact
                  </Button>
                  <Button
                    disableRipple
                    href={`${WP_URL}/bons-plans/`}
                    sx={{
                      textTransform: "none",
                      color: "#000",
                      fontFamily: '"Fira Sans", Sans-serif',
                      fontSize: "13px",
                      fontWeight: "500 !important",
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: "#007ACE",
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "3px",
                        backgroundColor: "transparent",
                        transform: "scaleX(0)",
                        transformOrigin: "center",
                        transition: "transform 0.3s ease-in-out",
                      },
                      "&:hover::after": {
                        backgroundColor: "#007ACE",
                        transform: "scaleX(1)",
                      },
                    }}
                  >
                    Bons plans
                  </Button>
                </Box>
              </Box>

              <IconButton
                disableRipple
                sx={{
                  color: "#007bff",
                  width: "2%",
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: "#ffffff",
                  },
                }}
                href={`${WP_URL}/mon-panier/`}
              >
                <Badge
                  badgeContent={cartCount}
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: "#d9534f",
                      color: "white",
                      fontSize: "12px",
                      minWidth: "18px",
                      height: "18px",
                    },
                  }}
                >
                  <img
                    loading="lazy"
                    src={`${process.env.PUBLIC_URL}/icons/cart_icon.svg`}
                    alt="Cart Icon"
                    style={{ width: 24, height: 24 }}
                  />
                </Badge>

              </IconButton>

            </Box>
          </Toolbar>
        </>
      )}
    </AppBar>
  );
}

export default ResponsiveAppBar;
